// The runtime's layer over an app (DESIGN §3.2, §8 R1c): a closed shadow
// root on a custom element appended to <html>, above everything the app
// draws, re-attached if the app removes it. The app's CSS can't reach
// inside it and nothing inside it styles the app.
//
// The app itself runs in a frame between the shortcut rows: #app is moved
// into #mt-frame, a fixed box with `contain: layout`, so the app's own
// position:fixed elements are placed inside the frame and never under a row.
// #app is the frame's scroller. The space left for the app's own safe-area
// padding is published on :root as --mt-inset-top/-bottom/-left/-right.

import { adoptCSS } from '../ui/styles.js';

export const HOST_TAG = 'mytheseus-shell';
const MAX_Z = '2147483647';

// Inline !important styles beat any app stylesheet that matches the host.
const HOST_STYLE = [
  'all:initial', 'position:fixed', 'inset:0', 'display:block', 'z-index:' + MAX_Z, 'pointer-events:none',
  'contain:strict', 'margin:0', 'padding:0', 'border:0', 'background:transparent', 'visibility:visible',
  'opacity:1', 'transform:none', 'filter:none', 'clip-path:none',
].map((d) => `${d} !important`).join(';');

/**
 * Creates the host and its closed shadow root. Returns {host, root, stop}.
 * `theme` copies a data-theme attribute from <html> (the screenshot harness
 * sets one) so :host([data-theme=dark]) applies.
 */
export function createHost(doc = document, css = '') {
  const host = doc.createElement(HOST_TAG);
  host.setAttribute('style', HOST_STYLE);
  const root = host.attachShadow({ mode: 'closed' });
  if (css) adoptCSS(root, css);
  const html = doc.documentElement;
  const syncTheme = () => {
    const t = html.dataset.theme;
    if (t) host.dataset.theme = t; else delete host.dataset.theme;
  };
  syncTheme();
  html.appendChild(host);
  // R1c: the app removing or restyling the host puts it straight back.
  const observer = new MutationObserver(() => {
    if (!host.isConnected || host.parentNode !== html) html.appendChild(host);
    if (host.getAttribute('style') !== HOST_STYLE) host.setAttribute('style', HOST_STYLE);
    syncTheme();
  });
  observer.observe(html, { childList: true, attributes: true, attributeFilter: ['data-theme'] });
  observer.observe(host, { attributes: true, attributeFilter: ['style'] });
  return { host, root, stop: () => observer.disconnect() };
}

export const FRAME_CSS = `
#mt-frame{position:fixed;left:0;right:0;top:var(--mt-frame-top,0px);bottom:var(--mt-frame-bottom,0px);contain:layout;overflow:hidden;margin:0;padding:0;}
#mt-frame>#app{position:absolute;inset:0;overflow:auto;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;}
#mt-frame[hidden]{display:none;}
`;

/** Wraps #app in the frame (idempotent). Returns the frame element. */
export function createFrame(doc = document, app = doc.getElementById('app')) {
  let frame = doc.getElementById('mt-frame');
  if (!frame) {
    frame = doc.createElement('div');
    frame.id = 'mt-frame';
    app.parentNode.insertBefore(frame, app);
    frame.appendChild(app);
    adoptCSS(doc, FRAME_CSS);
  }
  return frame;
}

/**
 * Publishes the rows' space: the frame's offsets and the insets the app
 * should still pad by (a row already covers the safe area on its edge).
 */
export function setInsets(doc, { top = 0, bottom = 0 }) {
  const s = doc.documentElement.style;
  s.setProperty('--mt-frame-top', `${top}px`);
  s.setProperty('--mt-frame-bottom', `${bottom}px`);
  s.setProperty('--mt-inset-top', top > 0 ? '0px' : 'env(safe-area-inset-top, 0px)');
  s.setProperty('--mt-inset-bottom', bottom > 0 ? '0px' : 'env(safe-area-inset-bottom, 0px)');
  s.setProperty('--mt-inset-left', 'env(safe-area-inset-left, 0px)');
  s.setProperty('--mt-inset-right', 'env(safe-area-inset-right, 0px)');
}
